import React, { useEffect, useMemo, useState } from "react";
import { useDispatch } from "react-redux";
import { motion } from "framer-motion";

import { Collapse, Stagger, StaggerItem } from "../../motion";
import { setAlert } from "../../../actions/alert";
import {
  fetchPackageHubs,
  resetPackageHubCoordinates,
  savePackageHubCoordinates,
} from "../../../actions/admin/packageHub";
import GoogleMapsScript from "./GoogleMapsScript";
import HubLocationMap from "./HubLocationMap";

// Shown on the package detail page when the package's pickup or drop-off tags
// name an airport/station hub (e.g. "haneda airport"). Places within 3km of the
// hub's point are priced with this package, so the admin can move that point here.
// The point is shared: every package with the same hub tag uses it.

const HUB_RADIUS_LABEL = "3km";

const isValidLatitude = (value) => Number.isFinite(value) && value >= -90 && value <= 90;
const isValidLongitude = (value) => Number.isFinite(value) && value >= -180 && value <= 180;

const buildGoogleMapsUrl = (lat, lon) => `https://www.google.com/maps?q=${lat},${lon}`;

// A hub belongs to this package when one of its tags is a pickup or drop-off tag.
const findHubsUsedByPackage = (hubs, pickupTags, dropoffTags) => {
  const packageTagLabels = [...(pickupTags || []), ...(dropoffTags || [])].map((tag) =>
    String(tag?.label || "")
      .toLowerCase()
      .trim(),
  );
  return hubs.filter((hub) => hub.packageTags.some((hubTag) => packageTagLabels.includes(hubTag)));
};

// The rows sit inside the package <form>. Pressing Enter in them must save the
// hub, not submit the whole package.
const runOnEnterKey = (action) => (event) => {
  if (event.key !== "Enter") return;
  event.preventDefault();
  action();
};

const HubCoordinateRow = ({ hub, mapStatus, onHubUpdated }) => {
  const dispatch = useDispatch();
  const [latitudeInput, setLatitudeInput] = useState(String(hub.lat));
  const [longitudeInput, setLongitudeInput] = useState(String(hub.lon));
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [errorMessage, setErrorMessage] = useState("");

  // Keep the inputs in step after a save or reset returns the stored point.
  useEffect(() => {
    setLatitudeInput(String(hub.lat));
    setLongitudeInput(String(hub.lon));
  }, [hub.lat, hub.lon]);

  const latitude = Number(latitudeInput);
  const longitude = Number(longitudeInput);
  const isLatitudeValid = latitudeInput.trim() !== "" && isValidLatitude(latitude);
  const isLongitudeValid = longitudeInput.trim() !== "" && isValidLongitude(longitude);
  const hasChanges = latitude !== hub.lat || longitude !== hub.lon;
  const canSave = isLatitudeValid && isLongitudeValid && hasChanges && !isSubmitting;

  const submitHubChange = async (requestHubChange, successMessage) => {
    setIsSubmitting(true);
    setErrorMessage("");
    try {
      const updatedHub = await requestHubChange();
      onHubUpdated(updatedHub);
      dispatch(setAlert(successMessage, "success"));
    } catch (error) {
      setErrorMessage(error.message);
    } finally {
      setIsSubmitting(false);
    }
  };

  const saveCoordinates = () => {
    if (!canSave) return;
    submitHubChange(
      () => savePackageHubCoordinates({ code: hub.code, lat: latitude, lon: longitude }),
      `${hub.name} location saved.`,
    );
  };

  const resetToDefault = () => {
    if (isSubmitting) return;
    submitHubChange(
      () => resetPackageHubCoordinates({ code: hub.code }),
      `${hub.name} location reset to default.`,
    );
  };

  // Picking on the map only fills the inputs; nothing is stored until Save.
  const fillInputsFromMap = (pickedLatitude, pickedLongitude) => {
    setLatitudeInput(String(pickedLatitude));
    setLongitudeInput(String(pickedLongitude));
    setErrorMessage("");
  };

  const discardChanges = () => {
    setLatitudeInput(String(hub.lat));
    setLongitudeInput(String(hub.lon));
    setErrorMessage("");
  };

  const previewLatitude = isLatitudeValid ? latitude : hub.lat;
  const previewLongitude = isLongitudeValid ? longitude : hub.lon;

  return (
    <div className="tw:rounded tw:border tw:border-gray-200 tw:p-3">
      <div className="tw:mb-2 tw:flex tw:flex-wrap tw:items-center tw:justify-between tw:gap-2">
        <div className="tw:flex tw:items-center tw:gap-2">
          <strong>{hub.name}</strong>
          <span className={`badge ${hub.isCustomized ? "bg-info text-dark" : "bg-secondary"}`}>
            {hub.isCustomized ? "Custom point" : "Default point"}
          </span>
        </div>
        <a
          href={buildGoogleMapsUrl(previewLatitude, previewLongitude)}
          target="_blank"
          rel="noopener noreferrer"
          className="tw:text-sm"
        >
          <i className="fa fa-map-marker fa-fw" aria-hidden="true"></i> View on map
        </a>
      </div>

      <HubLocationMap
        mapStatus={mapStatus}
        latitude={previewLatitude}
        longitude={previewLongitude}
        radiusMeters={hub.radiusMeters}
        onPick={fillInputsFromMap}
        isDisabled={isSubmitting}
      />
      <small className="tw:mt-1 tw:mb-2 tw:block tw:text-gray-500">
        Click the map or drag the pin to move the point, then press Save location. The circle is the area that
        gets the package price.
      </small>

      <div className="row g-2">
        <div className="col-12 col-sm-6">
          <label className="label mb-1" htmlFor={`hub-lat-${hub.code}`}>
            Latitude
          </label>
          <input
            id={`hub-lat-${hub.code}`}
            type="number"
            step="any"
            className={`form-control form-control-admin ${isLatitudeValid ? "" : "is-invalid"}`}
            value={latitudeInput}
            disabled={isSubmitting}
            onChange={(event) => setLatitudeInput(event.target.value)}
            onKeyDown={runOnEnterKey(saveCoordinates)}
          />
          <div className="invalid-feedback">Enter a number between -90 and 90.</div>
        </div>
        <div className="col-12 col-sm-6">
          <label className="label mb-1" htmlFor={`hub-lon-${hub.code}`}>
            Longitude
          </label>
          <input
            id={`hub-lon-${hub.code}`}
            type="number"
            step="any"
            className={`form-control form-control-admin ${isLongitudeValid ? "" : "is-invalid"}`}
            value={longitudeInput}
            disabled={isSubmitting}
            onChange={(event) => setLongitudeInput(event.target.value)}
            onKeyDown={runOnEnterKey(saveCoordinates)}
          />
          <div className="invalid-feedback">Enter a number between -180 and 180.</div>
        </div>
      </div>

      {hub.isCustomized && (
        <small className="tw:mt-1 tw:block tw:text-gray-500">
          Default: {hub.defaultLat}, {hub.defaultLon}
        </small>
      )}

      <Collapse isOpen={hasChanges && !isSubmitting}>
        <div className="alert alert-warning tw:mt-2 tw:mb-0 tw:py-2 tw:text-sm" role="status">
          Unsaved change. Press Save location to apply it to pricing.
        </div>
      </Collapse>

      <Collapse isOpen={Boolean(errorMessage)}>
        <div className="alert alert-danger tw:mt-2 tw:mb-0 tw:py-2" role="alert">
          {errorMessage}
        </div>
      </Collapse>

      <div className="tw:mt-2 tw:flex tw:flex-wrap tw:gap-2">
        <motion.button
          type="button"
          className="btn btn-primary btn-sm"
          disabled={!canSave}
          onClick={saveCoordinates}
          whileTap={canSave ? { scale: 0.96 } : undefined}
        >
          {isSubmitting ? "Saving..." : "Save location"}
        </motion.button>
        {hasChanges && (
          <motion.button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={isSubmitting}
            onClick={discardChanges}
            whileTap={isSubmitting ? undefined : { scale: 0.96 }}
          >
            Discard change
          </motion.button>
        )}
        {hub.isCustomized && (
          <motion.button
            type="button"
            className="btn btn-outline-secondary btn-sm"
            disabled={isSubmitting}
            onClick={resetToDefault}
            whileTap={isSubmitting ? undefined : { scale: 0.96 }}
          >
            Reset to default
          </motion.button>
        )}
      </div>
    </div>
  );
};

const PackageHubVicinityCard = ({ pickupTags, dropoffTags }) => {
  const [hubs, setHubs] = useState([]);
  const [loadStatus, setLoadStatus] = useState("loading"); // loading | ready | error
  const [loadErrorMessage, setLoadErrorMessage] = useState("");
  const [mapStatus, setMapStatus] = useState("loading"); // loading | ready | error

  useEffect(() => {
    // axios 0.21 cannot cancel a request, so a late answer is ignored after unmount.
    let isPageStillOpen = true;

    const loadHubs = async () => {
      try {
        const loadedHubs = await fetchPackageHubs();
        if (!isPageStillOpen) return;
        setHubs(loadedHubs);
        setLoadStatus("ready");
      } catch (error) {
        if (!isPageStillOpen) return;
        setLoadErrorMessage(error.message);
        setLoadStatus("error");
      }
    };

    loadHubs();
    return () => {
      isPageStillOpen = false;
    };
  }, []);

  const hubsUsedByPackage = useMemo(
    () => findHubsUsedByPackage(hubs, pickupTags, dropoffTags),
    [hubs, pickupTags, dropoffTags],
  );

  // Once Google Maps is needed it stays loaded: unmounting LoadScript deletes
  // window.google, so removing and re-adding a hub tag would break the map.
  const [shouldLoadMap, setShouldLoadMap] = useState(false);
  useEffect(() => {
    if (hubsUsedByPackage.length > 0) setShouldLoadMap(true);
  }, [hubsUsedByPackage.length]);

  const replaceHub = (updatedHub) =>
    setHubs((currentHubs) => currentHubs.map((hub) => (hub.code === updatedHub.code ? updatedHub : hub)));

  // Packages without an airport/station tag have no vicinity rule, so no card.
  const shouldShowCard = loadStatus !== "ready" || hubsUsedByPackage.length > 0;

  return (
    <>
      {/* Outside the Collapse so a collapsing card does not unload Google Maps. */}
      {shouldLoadMap && <GoogleMapsScript onStatusChange={setMapStatus} />}

      <Collapse isOpen={shouldShowCard}>
        <div className="card mb-3">
          <div className="card-header">Hub vicinity ({HUB_RADIUS_LABEL})</div>
          <div className="card-body">
            <p className="tw:text-sm tw:text-gray-600">
              Pickups and drop-offs within {HUB_RADIUS_LABEL} of this point get this package price. The point
              is shared by every package that uses the same airport or station tag.
            </p>

            {loadStatus === "loading" && <p className="tw:mb-0 tw:text-sm">Loading hub locations...</p>}

            {loadStatus === "error" && (
              <div className="alert alert-danger tw:mb-0" role="alert">
                {loadErrorMessage}
              </div>
            )}

            {loadStatus === "ready" && (
              <Stagger onMount className="tw:flex tw:flex-col tw:gap-3">
                {hubsUsedByPackage.map((hub) => (
                  <StaggerItem key={hub.code}>
                    <HubCoordinateRow hub={hub} mapStatus={mapStatus} onHubUpdated={replaceHub} />
                  </StaggerItem>
                ))}
              </Stagger>
            )}
          </div>
        </div>
      </Collapse>
    </>
  );
};

export default PackageHubVicinityCard;
